/**
 * Locale URL helpers (Iter 67dz — multi-locale SEO).
 *
 * Strategy: EN is canonical at root (`/cars`, `/car-rental/tirana-airport`).
 * Every non-EN locale lives under a path prefix (`/ru/cars`,
 * `/de/car-rental/tirana-airport`). We mount React Router with
 * `basename={prefix}` so the existing `<Link to="/cars">` calls keep
 * working without per-link rewrites — react-router strips the basename
 * before matching, and re-applies it when building hrefs.
 */
import { SUPPORTED_LANGUAGES } from "@/lib/i18n";

// Canonical public origin used in hreflang + canonical link tags. We use the
// production domain regardless of preview env so crawlers always see a stable
// URL — Google de-duplicates by canonical, so this is important.
export const CANONICAL_ORIGIN = "https://www.rentalcar.al";

// All locales registered in i18n, including EN.
const LOCALE_CODES = SUPPORTED_LANGUAGES.map((l) => l.code);

// Locales that get a URL prefix. EN is canonical → no prefix.
const PREFIXED_CODES = LOCALE_CODES.filter((c) => c !== "en");

/**
 * Returns the locale code embedded in the current URL pathname, or "en"
 * for the canonical (un-prefixed) root.
 */
export function detectLocaleFromPath(pathname) {
  const m = (pathname || "/").match(/^\/([a-z]{2})(\/|$)/i);
  if (m && PREFIXED_CODES.includes(m[1].toLowerCase())) {
    return m[1].toLowerCase();
  }
  return "en";
}

/**
 * BrowserRouter `basename` for the current URL. Empty string for EN.
 */
export function basenameForLocale(code) {
  return code && code !== "en" ? `/${code}` : "";
}

/**
 * Rebuild the current URL under a different locale (used by the
 * language switcher). Preserves the current page + query + hash; only
 * swaps the locale prefix. Returns a relative URL ready for
 * `window.location.assign`.
 */
export function switchLocaleUrl(targetCode) {
  const { pathname, search, hash } = window.location;
  const current = detectLocaleFromPath(pathname);
  const currentPrefix = basenameForLocale(current);
  const stripped = pathname.startsWith(currentPrefix)
    ? pathname.slice(currentPrefix.length) || "/"
    : pathname;
  const targetPrefix = basenameForLocale(targetCode);
  // Avoid producing `//cars` when stripped starts with "/".
  return `${targetPrefix}${stripped}${search}${hash}`;
}

/**
 * Build a fully-qualified canonical URL for a given path + locale, used
 * in the hreflang head tags. `path` should NOT include the locale prefix.
 */
export function localizedAbsoluteUrl(code, pathWithoutPrefix) {
  const prefix = basenameForLocale(code);
  const cleanPath = pathWithoutPrefix.startsWith("/")
    ? pathWithoutPrefix
    : `/${pathWithoutPrefix}`;
  // Drop the trailing slash on root paths so canonical doesn't differ from
  // what users actually visit ("/" stays as "/").
  return `${CANONICAL_ORIGIN}${prefix}${cleanPath}`.replace(/([^:])\/+$/, "$1");
}

/**
 * Return the "logical" path with the locale prefix removed. Used by
 * `LocaleSeo` to derive alternates for every supported locale.
 */
export function stripLocalePrefix(pathname) {
  const code = detectLocaleFromPath(pathname);
  const prefix = basenameForLocale(code);
  if (!prefix) return pathname || "/";
  return pathname.slice(prefix.length) || "/";
}

export { LOCALE_CODES, PREFIXED_CODES };
